import store from '@/store';
import {
  Action,
  Module,
  Mutation,
  VuexModule,
  } from 'vuex-module-decorators';
import {
  confirmUser,
  getPermission,
  login,
  page,
  passwordResetRequest,
  registerUser,
  resendLoginVerifyCode,
  resendRegistrationVerifyCode,
  setPassword,
  verifyPhoneByCode,
  verifyEmail,
} from '@/services/api';
import {
  LoginCredentialsType,
  IRegistration,
  IRegistrationTranslation } from '@/types';

const SET_REGISTRATION_TRANSLATION = 'SET_REGISTRATION_TRANSLATION';
const SET_USER_PHONE_NUMBER_ENTERED = 'SET_USER_PHONE_NUMBER_ENTERED';
const SET_GLOBAL_PERMISSIONS = 'SET_GLOBAL_PERMISSIONS';
const SET_2FA_CODE = 'SET_2FA_CODE';

const rawError: boolean = true;

@Module({namespaced: true})
export default class Accounts extends VuexModule implements IRegistration {
  public formErrorMessage = '';
  public userPhone: string = '';
  public permissions: string[] = [];
  public code2FA: string = '';

  public translation: IRegistrationTranslation = {
    messageConfirmPhone: {
      title: '',
      message: 'Congrats! you phone number confirm',
    },
    messageEmailSuccess: {
      title: '',
      message: 'Congrats! you email address confirm',
    },
    messageEmailFailed: {
      title: '',
      message: 'Ups, something wrong :(',
    },
    login: {
      title: '',
      loginLabel: '',
      passwordLabel: '',
      forgotPassword: '',
      buttonText: '',
      hint: '',
    },
    confirm: {
      title: '',
      subTitle: '',
      buttonText: '',
      backButtonName: '',
      hint: '',
      somethingWrong: '',
    },
    confirm2fa: {
      title: '',
      subTitle: '',
      buttonText: '',
      backButtonName: '',
      hint: '',
      somethingWrong: '',
    },
    signUp: {
      title: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password1: '',
      password2: '',
      isAgree: true,
      buttonName: '',
      agreement: '',
      hint: '',
      errorsMessages: {
        passwordNotMatch: '',
        fieldRequired: '',
        emailWrong: '',
        phoneWrong: '',
        agreementNotConfirm: '',
        minLength: '',
        maxLength: '',
        innerError: '',
        passwordTooSimple: '',
        passwordContent: '',
      },
    },
    passRecover: {
      title: '',
      email: '',
      description: '',
      agreement: '',
      buttonName: '',
      backButtonName: '',
      hint: '',
      errorsMessages: {
        emailWrong: '',
      },
    },
    setPassword: {
      title: '',
      passwordLabel: '',
      buttonName: '',
      errorsMessages: {},
    },
    messageSendMail: {
      title: '',
      message: '',
      phrase: '',
      errorsMessages: {},
    },
    messageSuccess: {
      title: '',
      message: '',
      buttonName: '',
      errorsMessages: {},
    },
    confirmEmail: {
      title: '',
      emailLabel: '',
      buttonName: '',
      errorsMessages: {},
    },
    messageSuccessEmailConfirm: {
      title: '',
      message: '',
      buttonName: '',
      errorsMessages: {},
    },
    passwordResetSuccess: {
      title: '',
      message: '',
      errorsMessages: {},
    },
  };

  public get globalPermissions(): string[] {
    return this.permissions;
  }

  public get translations(): {} {
    return this.translation;
  }

  public get errorMessage(): {} {
    return this.formErrorMessage;
  }

  public get login2faCode(): {} {
    return this.code2FA;
  }

  @Mutation
  public [SET_REGISTRATION_TRANSLATION](payload: IRegistrationTranslation): void {
    this.translation = {...this.translation, ...payload };
  }

  @Mutation
  public [SET_2FA_CODE](payload: string): void {
    this.code2FA = payload;
  }

  @Mutation
  public [SET_USER_PHONE_NUMBER_ENTERED](phone: string): void {
    this.userPhone = phone;
  }

  @Mutation
  public [SET_GLOBAL_PERMISSIONS](payload: string[]): void {
    this.permissions = payload;
  }

  public get userEnterPhone() {
    return this.userPhone;
  }

  @Action({commit: SET_2FA_CODE})
  public set2faCode(code) {
    return code;
  }

  @Action({commit: SET_USER_PHONE_NUMBER_ENTERED})
  public setUserPhoneEntered(phone) {
    return phone;
  }

  @Action({commit: SET_REGISTRATION_TRANSLATION, rawError})
  public async fetchTranslation() {
    return await page('accounts');
  }

  @Action({ rawError })
  public async login(credentials: LoginCredentialsType) {
    return login(credentials).then((response) => {
      return response;
    }).catch((error) => console.error(error));
  }

  @Action({ rawError })
  public async verifyUserPhoneByCode(data) {
    return verifyPhoneByCode(data).then((response) => {
      return response;
    }).catch((error) => console.error(error));
  }

  @Action({ rawError })
  public async confirm2FA(data) {
    return confirmUser(data).then((response) => {
      return response;
    }).catch((error) => console.error(error));
  }

  @Action({ rawError })
  public async resendLoginVerifyByPhoneCode(uuid) {
    return resendLoginVerifyCode({id: uuid}).then((response) => {
      return response;
    }).catch((error) => console.error(error));
  }

  @Action({ rawError })
  public async resendRegistrationVerifyByPhoneCode(uuid) {
    return resendRegistrationVerifyCode({id: uuid}).then((response) => {
      return response;
    }).catch((error) => console.error(error));
  }

  @Action({ rawError })
  public async createNewUser(data) {
    return registerUser(data).then((response) => {
      return response;
    }).catch((error) => console.error(error));
  }

  @Action({ rawError })
  public async resetPassword(email) {
    return passwordResetRequest(email).then((response) => {
      return response;
    }).catch((error) => console.error(error));
  }

  @Action({rawError})
  public sendPassword(data) {
    return setPassword(data);
  }

  @Action({rawError, commit: SET_GLOBAL_PERMISSIONS})
  public async getGlobalPermissions() {
    const currentNavigation = this.context?.rootGetters?.navigation;
    const data = await getPermission();
    store.dispatch('setNavigation', {navigation: currentNavigation, permissions: data});
    return !!data ? data : this.globalPermissions;
  }

  @Action({ rawError })
  public verifyUserEmail({id, key}) {
    return verifyEmail(id, key);
  }
}
